/**
 * @file HighlightMatch.tsx
 * @brief Renders text with query matches emphasized in brand red.
 *
 * @details
 * Used by the Cmd+K palette so typed characters stand out in result rows.
 * Case-insensitive; escapes regex special chars in the query.
 *
 * @author DES
 * @date 17-07-2026
 */

import { Fragment, type ReactNode } from "react";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function HighlightMatch({
  text,
  query,
}: {
  text: string;
  query: string;
}): ReactNode {
  const trimmed = query.trim();
  if (!trimmed) return text;

  const parts = text.split(new RegExp(`(${escapeRegExp(trimmed)})`, "gi"));
  if (parts.length === 1) return text;

  return parts.map((part, index) => {
    if (part.toLowerCase() === trimmed.toLowerCase()) {
      return (
        <mark
          key={`m-${index}`}
          className="bg-transparent p-0 font-semibold text-brand"
        >
          {part}
        </mark>
      );
    }
    return <Fragment key={`t-${index}`}>{part}</Fragment>;
  });
}
